import { PremiumBackground } from '../effects/PremiumBackground';
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, BookOpen, ShieldCheck, ArrowDown } from 'lucide-react';
export interface StrategicInquiriesProps {
  id?: string;
}

export const StrategicInquiries: React.FC<StrategicInquiriesProps> = ({ id = 'faq' }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [selectedGlossary, setSelectedGlossary] = useState<string | null>(null);

  const faqs = [
    {
      q: 'Узнают ли мои врачи, конкуренты и пациенты о том, что я продаю бизнес?',
      a: 'Исключено. Мы работаем по протоколу Zero Data Breach. Никаких досок объявлений и массовых рассылок. На рынок выпускается «Слепой Тизер» (Blind Teaser) без названия клиники, фотографий и гео-меток. Доступ к цифрам открывается только профильным федеральным фондам и сетям после проверки их службы безопасности и подписания жесткого соглашения о неразглашении (Mutual NDA) со штрафом от 5 000 000 рублей за факт утечки.'
    },
    {
      q: 'Как вы гарантируете безопасность передачи наших баз 1С и МИС для аудита?',
      a: 'Протокол Air-Gapped Security. Мы не подключаемся к вашим живым серверам. Вы передаете нам обезличенные дампы баз (.dt) без персональных данных пациентов (деперсонификация по 152-ФЗ). Наши аналитики разворачивают их в полностью изолированном защищенном контуре бутика. По завершении аудита виртуальная среда уничтожается с выдачей вам криптографического акта об удалении данных.'
    },
    {
      q: 'Как формируется ваш гонорар? Вы берете деньги только за результат?',
      a: 'Наша архитектура состоит из двух фаз. Фаза 1 (Retainer): Фиксированный аванс за независимый форензик-аудит (604 точки), нормализацию вашей EBITDA и сборку защищенной комнаты данных (VDR). Фаза 2 (Success Fee): Процент от реальной суммы, полученной вами в день закрытия сделки. Мы не классические брокеры, работающие «только за процент», потому что мы не рассылаем спам-презентации. Мы проводим тяжелую финансовую инженерию, которая окупается в 10–20 раз на этапе переговоров, защищая вас от вычетов и дисконтов.'
    },
    {
      q: 'Почему процесс подготовки и сделки занимает от 4 до 6 месяцев?',
      a: 'Быстрая сделка — это сделка с колоссальным штрафным дисконтом для продавца. 3–5 недель занимает наша процедура очистки и сборки математической модели. Еще 4–6 недель уходит на защиту вашей цены перед инвест-комитетами фондов. Оставшееся время требуется на юридическое конструирование обязывающего договора (SPA), структурирование безопасных расчетов и снятие с вас рисков субсидиарной ответственности.'
    }
  ];

  const glossary = [
    {
      term: 'EBITDA',
      title: 'EBITDA:',
      def: 'Реальная операционная прибыль вашей клиники до налогов и кредитов. Главная цифра, на которую смотрят покупатели и умножают на мультипликатор (от 4.0x до 7.5x).'
    },
    {
      term: 'Add-backs',
      title: 'Add-backs (Возврат расходов):',
      def: 'Ваши личные траты (автомобиль, поездки, строительство), которые бухгалтер списал на расходы клиники. Мы легально возвращаем их в расчет прибыли, доказывая инвестору, что реальная рентабельность бизнеса выше.'
    },
    {
      term: 'Deferred Revenue',
      title: 'Deferred Revenue (Отложенная выручка / Авансы):',
      def: 'Предоплаты пациентов за брекеты или имплантацию. Услуга еще не оказана. Инвесторы считают это вашим долгом и пытаются вычесть из цены клиники рубль в рубль.'
    },
    {
      term: 'NWC Peg',
      title: 'NWC Peg (Целевой оборотный капитал):',
      def: 'Норматив денег и материалов, который должен остаться в клинике в день ее передачи покупателю, чтобы бизнес не остановился на следующий день.'
    },
    {
      term: 'Earn-Out',
      title: 'Earn-Out (Эрн-аут / Отложенный платеж):',
      def: 'Покупатель отдает вам часть суммы (например, 70%) сразу, а 30% замораживает на 2 года и выплатит только в том случае, если ключевые врачи не уволятся, а клиника выполнит план.'
    },
    {
      term: 'Specific Indemnity',
      title: 'Specific Indemnity (Возмещение потерь по ст. 406.1 ГК РФ):',
      def: 'Пункт договора, по которому вы лично обязаны возместить покупателю ущерб из своего кармана, если из-за ваших старых нарушений (дробление, налоги, лицензия) клинику оштрафуют после продажи.'
    },
    {
      term: 'Tax Escrow',
      title: 'Tax Escrow (Налоговый эскроу):',
      def: 'Временная заморозка части ваших денег (10–15%) на счете в банке на 1–3 года на случай, если налоговая придет с проверкой за старые периоды.'
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <section
      id={id}
      aria-label="Протоколы взаимодействия и глоссарий M&A"
      className="relative w-full bg-[#FAF8F5] text-[#141416] py-24 md:py-32 border-t border-[#EAE5DE] font-sans overflow-hidden"
    >
      {/* ТАКТИЛЬНЫЙ МАКРО-РЕЛЬЕФ */}
      <PremiumBackground variant={7} />

      
      

      {/* Light subtle grid pattern */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(140,109,55,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(140,109,55,0.02)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_80%)]" />
      </div>

      {/* JSON-LD Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        
        {/* =========================================================================
            1. НАДЗАГОЛОВОК, 2. ЗАГОЛОВОК H2
            ========================================================================= */}
        <div className="max-w-4xl mb-16 flex flex-col gap-6 relative z-10">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] text-[#8C6D37] uppercase tracking-[0.2em] font-bold flex items-center gap-2 bg-[#8C6D37]/10 border border-[#8C6D37]/30 px-3 py-1 rounded-[2px]">
              <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37]" />
              ЭТАЖ 15 // FAQ & GLOSSARY // РЕГЛАМЕНТ СДЕЛОК
            </span>
            <div className="h-px w-12 bg-[#EAE5DE]" />
            <span className="font-mono text-[10px] text-[#64748B] uppercase tracking-[0.15em] bg-white border border-[#EAE5DE] px-2.5 py-1 rounded-[2px]">
              DECISION PROTOCOLS
            </span>
          </div>

          <h2 className="text-3xl sm:text-5xl lg:text-[54px] font-serif font-semibold text-[#141416] tracking-[-0.02em] leading-[1.08] mb-2">
            Протоколы взаимодействия <br className="hidden sm:block" />
            <span className="text-[#8C6D37] italic font-serif">и словарь инвестора.</span>
          </h2>

          <div className="relative pl-5 py-3 border-l-[4px] border-[#8C6D37] bg-[#F7F4EE]/60 rounded-r-[2px] border-y border-r border-[#EAE5DE] max-w-2xl">
            <p className="text-[16px] sm:text-[17px] text-[#334155] leading-[1.7] font-normal">
              Ответы на критические вопросы фаундеров и расшифровка терминологии, которую используют институциональные покупатели для снижения оценки.
            </p>
          </div>
        </div>

        {/* =========================================================================
            СЕТКА ВЕРСТКИ:
            Левая колонка (45% ширины): Аккордеон вопросов (FAQ)
            Правая колонка (55% ширины): Встроенный словарь терминов (Glossary)
            ========================================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start mb-16 relative z-10">
          
          {/* Левая колонка: 45% (lg:col-span-5) - FAQ Accordion */}
          <div className="lg:col-span-5 space-y-6">
            <div className="pb-4 border-b border-[#EAE5DE] flex items-center justify-between">
              <span className="font-mono text-[11px] font-bold text-[#8C6D37] uppercase tracking-[0.15em] flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                КРИТИЧЕСКИЕ ВОПРОСЫ
              </span>
              <span className="font-mono text-[10px] text-[#64748B] tracking-[0.15em] bg-white border border-[#EAE5DE] px-2.5 py-1 rounded-[2px] font-bold">
                04 PROTOCOLS
              </span>
            </div>

            <div className="divide-y divide-[#EAE5DE] border-t border-[#EAE5DE]">
              {faqs.map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                return (
                  <div key={idx} className="group relative overflow-hidden transition-all duration-300 bg-white hover:bg-[#FAF8F5]">
                    {/* Active left indicator bar */}
                    <div className={`absolute top-0 left-0 w-[3px] h-full transition-all duration-300 ${
                      isOpen ? 'bg-[#8C6D37]' : 'bg-transparent group-hover:bg-[#8C6D37]/30'
                    }`} />
                    
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                      className="relative z-10 w-full text-left py-6 sm:py-7 px-5 flex items-start justify-between gap-4 cursor-pointer transition-colors"
                      aria-expanded={isOpen}
                    >
                      <span className={`text-[17px] sm:text-[18px] font-serif font-semibold leading-[1.35] tracking-tight transition-colors duration-300 ${isOpen ? 'text-[#8C6D37]' : 'text-[#141416] group-hover:text-[#8C6D37]'}`}>
                        {faq.q}
                      </span>
                      <span className={`shrink-0 mt-0.5 w-7 h-7 rounded-[2px] flex items-center justify-center transition-all duration-300 border ${
                        isOpen 
                          ? 'bg-[#8C6D37] text-white rotate-180 border-[#8C6D37]' 
                          : 'bg-[#F7F4EE] text-[#64748B] border-[#EAE5DE] group-hover:border-[#8C6D37]/40 group-hover:text-[#141416]'
                      }`}>
                        <ChevronDown className="w-4 h-4 transition-transform duration-300" />
                      </span>
                    </button>

                    <div 
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        isOpen ? 'max-h-[500px] opacity-100 pb-7 px-5' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="text-[14px] sm:text-[15px] text-[#334155] leading-[1.75] font-normal border-l-2 border-[#8C6D37] pl-4 py-2 bg-[#F7F4EE]/50 rounded-r-[2px] border-y border-r border-[#EAE5DE]">
                        <p>{faq.a}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Правая колонка: 55% (lg:col-span-7) - M&A-Словарь для Фаундера (Glossary) */}
          <div className="lg:col-span-7 space-y-6">
            <div className="pb-4 border-b border-[#EAE5DE] flex items-center justify-between">
              <span className="font-mono text-[11px] font-bold text-[#8C6D37] uppercase tracking-[0.15em] flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                M&A-СЛОВАРЬ (GLOSSARY)
              </span>
              <span className="font-mono text-[10px] text-[#64748B] tracking-[0.15em] bg-white border border-[#EAE5DE] px-2.5 py-1 rounded-[2px] uppercase font-bold">
                Язык инвест-комитетов
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
              {glossary.map((item, idx) => {
                const isHighlighted = selectedGlossary === item.term;
                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedGlossary(isHighlighted ? null : item.term)}
                    className={`group relative rounded-[2px] p-6 sm:p-7 border transition-all duration-300 cursor-pointer overflow-hidden ${
                      isHighlighted
                        ? 'border-[#8C6D37] bg-white shadow-sm ring-1 ring-[#8C6D37]'
                        : 'border-[#EAE5DE] hover:border-[#CBD5E1] bg-white shadow-xs'
                    }`}
                  >
                    {/* Active highlight bar */}
                    <div className={`absolute top-0 left-0 w-[3px] h-full transition-all duration-300 ${
                      isHighlighted ? 'bg-[#8C6D37]' : 'bg-transparent group-hover:bg-[#8C6D37]/30'
                    }`} />

                    <div className="relative z-10 pl-2">
                      <div className="flex items-center justify-between mb-3">
                        <span className={`font-mono text-[10px] font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${isHighlighted ? 'text-[#8C6D37]' : 'text-[#64748B] group-hover:text-[#8C6D37]'}`}>
                          {item.term}
                        </span>
                        {isHighlighted && <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37]" />}
                      </div>
                      
                      <div className="text-[14px] sm:text-[15px] text-[#334155] leading-[1.7]">
                        <strong className={`font-serif font-semibold text-lg sm:text-[20px] block mb-2 transition-colors duration-300 ${
                          isHighlighted ? 'text-[#141416]' : 'text-[#141416] group-hover:text-[#8C6D37]'
                        }`}>
                          {item.title}
                        </strong>
                        <span className={`font-normal transition-colors duration-300 block border-t border-[#EAE5DE] pt-3 ${
                          isHighlighted ? 'text-[#334155]' : 'text-[#64748B]'
                        }`}>
                          {item.def}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};
